import { AutoComplete, Input } from 'antd';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CHAT_INPUT_MODES } from '@guuru/chat-web';
import ChatMessageSubmitButton from './ChatMessageSubmitButton';
import InnerLeftButtons from './InnerLeftButtons';
import LeftIcon from './LeftIcon';

const mapResponsesToOptions = (responses) =>
  responses.map(({ command, response }) => ({
    value: response,
    label: (
      <div>
        <strong>/{command}</strong>
        <br />
        {response}
      </div>
    ),
  }));

const TextInput = function ({
  chatId = null,
  isVisible = false,
  isEnabled,
  isValid,
  isUser,
  text,
  noFocus = false,
  partnerId = '',
  onAttachmentSubmit,
  hasTextAction = null,
  inputMode = '',
  cannedResponses = [],
  inputState = '',
  handleInputFocus = () => {},
  handleInput,
  submit,
}) {
  const inputRef = useRef();
  const { t } = useTranslation();
  const cannedResponseOptions = useMemo(
    () => mapResponsesToOptions(cannedResponses),
    [cannedResponses],
  );

  const handleSubmitClick = useCallback(() => {
    submit('Submit Button press', text);
  }, [submit, text]);

  useEffect(() => {
    if (!isVisible) return;

    const action = noFocus ? inputRef.current?.blur : inputRef.current?.focus;
    action?.();
  }, [chatId, isVisible, noFocus]);

  const handleSelect = (txt) => {
    handleInput(txt, false);
    submit('Submit Canned response', text);
  };

  const handlePressEnter = (event) => {
    event.preventDefault();
    submit('Enter press', text);
  };

  /** Handler for input text change */
  const handleChange = (newText) => {
    handleInput(newText, true);
  };

  const handleChangeEvent = (event) => {
    const { value } = event.target;
    return handleInput(value, true);
  };

  const getPlaceholder = () => {
    switch (inputMode) {
      case CHAT_INPUT_MODES.question:
        return t('chat:::placeholderQuestion');
      case CHAT_INPUT_MODES.name:
        return t('chat:::placeholderName');
      case CHAT_INPUT_MODES.email:
        return t('chat:::placeholderEmail');
      case CHAT_INPUT_MODES.chat:
        return t('chat:::placeholderChat');
      default:
        return !isUser ? t('chat:::placeholderChat') : undefined;
    }
  };

  const getInput = () => {
    switch (inputMode) {
      case CHAT_INPUT_MODES.email:
        return (
          <Input
            autoFocus={isVisible}
            className="chat-input"
            placeholder={getPlaceholder()}
            value={text}
            onFocus={handleInputFocus}
            onPressEnter={handlePressEnter}
            onChange={handleChangeEvent}
            disabled={!!inputState}
            type="email"
            style={{ fontFamily: 'inherit' }}
          />
        );
      default:
        return (
          <>
            <InnerLeftButtons
              visible={inputMode === CHAT_INPUT_MODES.chat}
              handleInput={handleInput}
              text={text}
              inputRef={inputRef.current}
              partnerId={partnerId}
              hasTextAction={hasTextAction}
              onAttachmentSubmit={onAttachmentSubmit}
            />
            <AutoComplete
              autoFocus={isVisible}
              defaultActiveFirstOption
              className="chat-input"
              options={cannedResponseOptions}
              onChange={handleChange}
              filterOption={false}
              onSelect={handleSelect}
              value={text}
              onFocus={handleInputFocus}
            >
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 6 }}
                className="chat-input"
                disabled={!!inputState}
                onPressEnter={handlePressEnter}
                placeholder={getPlaceholder()}
                style={{ fontFamily: 'inherit' }}
                type="text"
                ref={inputRef}
              />
            </AutoComplete>
          </>
        );
    }
  };

  return (
    <>
      <LeftIcon inputMode={inputMode} />
      {getInput()}
      <div
        style={{ display: 'flex', alignItems: 'center' }}
        className="chat-input-actions"
      >
        <ChatMessageSubmitButton
          isEnabled={isEnabled}
          isValid={isValid}
          onClick={handleSubmitClick}
        />
      </div>
    </>
  );
};

TextInput.propTypes = {
  chatId: PropTypes.string,
  isVisible: PropTypes.bool,
  isEnabled: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  isUser: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
  noFocus: PropTypes.bool,
  partnerId: PropTypes.string,
  onAttachmentSubmit: PropTypes.func.isRequired,
  hasTextAction: PropTypes.func,
  inputMode: PropTypes.string,
  cannedResponses: PropTypes.arrayOf(
    PropTypes.shape({
      response: PropTypes.string,
      command: PropTypes.string,
    }),
  ),
  inputState: PropTypes.string,
  handleInputFocus: PropTypes.func,
  handleInput: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
};

export default TextInput;
